import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../context/AdminContext';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Eye, Loader2, Plus, Pencil, Trash2, Star } from 'lucide-react';
import { toast } from 'sonner';

export const AdminCustomers = () => {
  const { apiCall } = useAdmin();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const data = await apiCall('/admin/customers');
      setCustomers(data.customers || []);
    } catch (err) {
      toast.error('Failed to fetch customers');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-customers">
      <div>
        <h2 className="text-2xl font-heading font-bold text-[#1A1625]">Customers</h2>
        <p className="text-muted-foreground">Manage customer accounts</p>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center"><Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" /></div>
          ) : customers.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">No customers yet</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left py-3 px-4">Name</th>
                    <th className="text-left py-3 px-4">Email</th>
                    <th className="text-left py-3 px-4">Phone</th>
                    <th className="text-left py-3 px-4">Joined</th>
                    <th className="text-left py-3 px-4">Type</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.map((customer) => (
                    <tr key={customer.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4 font-medium">{customer.first_name} {customer.last_name}</td>
                      <td className="py-3 px-4">{customer.email}</td>
                      <td className="py-3 px-4 text-muted-foreground">{customer.phone || '-'}</td>
                      <td className="py-3 px-4 text-muted-foreground">
                        {new Date(customer.created_at).toLocaleDateString()}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                          customer.is_guest ? 'bg-gray-100 text-gray-700' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {customer.is_guest ? 'Guest' : 'Registered'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export const AdminPayments = () => {
  const { apiCall } = useAdmin();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    try {
      const data = await apiCall('/admin/payments');
      setPayments(data.payments || []);
    } catch (err) {
      toast.error('Failed to fetch payments');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-payments">
      <div>
        <h2 className="text-2xl font-heading font-bold text-[#1A1625]">Payments</h2>
        <p className="text-muted-foreground">Track payment transactions</p>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center"><Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" /></div>
          ) : payments.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">No payments yet</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left py-3 px-4">Session ID</th>
                    <th className="text-left py-3 px-4">Order ID</th>
                    <th className="text-left py-3 px-4">Amount</th>
                    <th className="text-left py-3 px-4">Status</th>
                    <th className="text-left py-3 px-4">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((payment) => (
                    <tr key={payment.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4 font-mono text-xs">{payment.session_id?.substring(0, 20)}...</td>
                      <td className="py-3 px-4 text-muted-foreground">{payment.order_id?.substring(0, 8)}...</td>
                      <td className="py-3 px-4 font-medium">{payment.amount?.toFixed(2)} {payment.currency?.toUpperCase()}</td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                          payment.payment_status === 'paid' ? 'bg-green-100 text-green-700' :
                          payment.payment_status === 'initiated' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {payment.payment_status}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-muted-foreground">
                        {new Date(payment.created_at).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export const AdminReviews = () => {
  const { apiCall } = useAdmin();
  const [reviews, setReviews] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [formData, setFormData] = useState({
    product_id: '',
    customer_name: '',
    rating: 5,
    comment: '',
    is_approved: true,
  });

  useEffect(() => {
    fetchReviews();
    fetchProducts();
  }, []);

  const fetchReviews = async () => {
    try {
      const data = await apiCall('/admin/reviews');
      setReviews(data || []);
    } catch (err) {
      toast.error('Failed to fetch reviews');
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const data = await apiCall('/products?limit=1000');
      setProducts(data.products || []);
    } catch (err) {
      console.error('Failed to fetch products');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingReview) {
        await apiCall(`/admin/reviews/${editingReview.id}`, {
          method: 'PUT',
          body: JSON.stringify(formData),
        });
        toast.success('Review updated');
      } else {
        await apiCall('/admin/reviews', {
          method: 'POST',
          body: JSON.stringify(formData),
        });
        toast.success('Review created');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchReviews();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleEdit = (review) => {
    setEditingReview(review);
    setFormData({
      product_id: review.product_id || '',
      customer_name: review.customer_name || '',
      rating: review.rating || 5,
      comment: review.comment || '',
      is_approved: review.is_approved ?? true,
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingReview(null);
    setFormData({
      product_id: '',
      customer_name: '',
      rating: 5,
      comment: '',
      is_approved: true,
    });
  };

  const toggleApproval = async (reviewId, isApproved) => {
    try {
      await apiCall(`/admin/reviews/${reviewId}`, {
        method: 'PUT',
        body: JSON.stringify({ is_approved: !isApproved }),
      });
      toast.success('Review updated');
      fetchReviews();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const deleteReview = async (reviewId) => {
    if (!window.confirm('Delete this review?')) return;
    try {
      await apiCall(`/admin/reviews/${reviewId}`, { method: 'DELETE' });
      toast.success('Review deleted');
      fetchReviews();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const getProductName = (productId) => {
    const product = products.find(p => p.id === productId);
    return product ? (product.name?.ro || product.name?.en || 'Unknown') : 'Unknown Product';
  };

  return (
    <div className="space-y-6" data-testid="admin-reviews">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-heading font-bold text-[#1A1625]">Reviews</h2>
          <p className="text-muted-foreground">Manage product reviews</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-primary"><Plus className="mr-2 h-4 w-4" /> Add Review</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingReview ? 'Edit Review' : 'Add Review'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Product</Label>
                <Select value={formData.product_id} onValueChange={(value) => setFormData({ ...formData, product_id: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a product" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {products.slice(0, 100).map((product) => (
                      <SelectItem key={product.id} value={product.id}>
                        {product.name?.ro || product.name?.en || product.id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Customer Name</Label>
                <Input
                  value={formData.customer_name}
                  onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Rating</Label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setFormData({ ...formData, rating: star })}
                      className={`text-2xl ${star <= formData.rating ? 'text-yellow-400' : 'text-gray-300'}`}
                    >
                      ★
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label>Comment</Label>
                <Textarea
                  value={formData.comment}
                  onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                  rows={3}
                  required
                />
              </div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.is_approved}
                  onChange={(e) => setFormData({ ...formData, is_approved: e.target.checked })}
                  className="w-4 h-4"
                />
                <span>Approved (visible on site)</span>
              </label>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-primary">{editingReview ? 'Update' : 'Create'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center"><Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" /></div>
          ) : reviews.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">No reviews yet</div>
          ) : (
            <div className="divide-y">
              {reviews.map((review) => (
                <div key={review.id} className="p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <p className="font-medium">{review.customer_name}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${review.is_approved ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                          {review.is_approved ? 'Approved' : 'Pending'}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mb-1">
                        Product: {getProductName(review.product_id)}
                      </p>
                      <div className="flex items-center gap-1 my-1">
                        {[...Array(5)].map((_, i) => (
                          <span key={i} className={i < review.rating ? 'text-gold' : 'text-gray-300'}>★</span>
                        ))}
                      </div>
                      <p className="text-muted-foreground text-sm">{review.comment}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEdit(review)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant={review.is_approved ? 'outline' : 'default'}
                        onClick={() => toggleApproval(review.id, review.is_approved)}
                      >
                        {review.is_approved ? 'Hide' : 'Approve'}
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => deleteReview(review.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminCustomers;
